<template>
  <div id="app-root" class="h-100 d-flex flex-column">
    <component :is="disconnectedComponent" :class="{ 'hideDisconnected': !isAppDisconnected }"></component>

    <router-view v-if="!isAppDisconnected" v-slot="{ Component, route }">
      <keep-alive v-if="useKeepAlive(route)">
        <component :is="Component" v-if="Component" :key="route.name" />
      </keep-alive>
      <component v-else-if="Component" :is="Component" :key="route.name" />
    </router-view>
    <modal-call-agent v-if="$store.getters.getUser"></modal-call-agent>
    <div v-if="$store.getters.getUser" >
      <div v-for="(notification, index) in notifications" :key="index">
        <download-scriptix-transcription
        :key="getUniqueKey(notification, 'download-scriptix-transcription')"
        :notificationCount="notifications.length"
        :index="index"
        :notification="notification"
        @close="removeNotification(index)"
        @triggerScriptixTranscription="triggerScriptixTranscription"></download-scriptix-transcription>
      </div>
      <div class="notification-count" v-if="notifications.length > 1">{{ notifications.length }}</div>
      <Toast />
    </div>
  <Toast />
    <multi-step-upload ref="uploadProgress" />
    <component-lab-button v-if="$store.getters.getUser" />
    <join-troubleshoot-button />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import Moment from 'moment-timezone'
import Toast from 'primevue/toast'
import appRootMixin from './mixins/appRoot'

export default {
  name: 'App',
  mixins: [appRootMixin],
  data () {
    return {
      notifications: [],
      progress: 0,
      usedKeys: new Set()
    }
  },
  components: {
    disconnected: defineAsyncComponent(() => import('./components/MeetingDisconnected.vue')),
    'component-lab-button': defineAsyncComponent(() => import('./components/internal/ComponentLabButton.vue')),
    Toast
  },
  computed: {
    disconnectedComponent () {
      return 'disconnected'
    },
    isDevelopment () {
      return process.env.NODE_ENV === 'development'
    },
  },
  methods: {
    useKeepAlive (route) {
      return this.$store.getters.getProduct === 'personal' &&
        ['meeting', 'appointment', 'waiting_room'].includes(route.name)
    },
    async testProgressUI () {
      const tracker = this.$refs.uploadProgress

      // Start
      tracker.start()

      // Step 1: Requesting token
      await this.wait(1500)

      // Step 2: Token received
      tracker.nextStep()
      await this.wait(1000)

      // Step 3: Upload progress
      tracker.nextStep()
      for (let i = 0; i <= 100; i += 5) {
        tracker.setUploadProgress(i)
        await this.wait(100)
      }

      // Step 4: Complete
      tracker.complete()
    },

    wait (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    // DEBUG: Method to test Scriptix transcription dialog
    testScriptixDialog () {
      console.log('Testing Scriptix transcription dialog...')
      const mockTranscription = {
        data: {
          'x-scriptix-session': 'test-session-' + Date.now(),
          'x-scriptix-document': 'test-document-' + Date.now()
        }
      }
      this.downloadTranscription(mockTranscription)
    },
    getUniqueKey (notification, label = '') {
      const key = notification.data['x-scriptix-document'] || notification.data['x-scriptix-session']
      if (this.usedKeys.has(key)) {
        return null
      }
      this.usedKeys.add(key)
      return `${label}-${key}`
    },
    removeNotification (index) {
      this.notifications.splice(index, 1)
    },
    downloadTranscription (transcription) {
      if (!this.$store.getters.getUser) {
        return
      }
      const key = transcription.data['x-scriptix-document'] || transcription.data['x-scriptix-session']
      if (this.usedKeys.has(key)) {
        return
      }
      this.notifications.push({ ...transcription, progress: this.progress })
    },
    onBumicomTranscriptionReady (data) {
      if (!this.$store.getters.getUser) {
        return
      }

      // Calculate expiration time remaining
      const expiresAt = new Date(data.expiresAt)
      const now = new Date()
      const minutesRemaining = Math.ceil((expiresAt - now) / (1000 * 60))
      const expirationText = minutesRemaining > 0 ? `Expires in ${minutesRemaining} minutes` : 'Link expired'

      // Show toast notification with download link
      this.$toast.add({
        severity: 'success',
        summary: 'Transcription Ready',
        detail: `PDF ready to download. ${expirationText}`,
        life: 8000,
        closable: true
      })
      window.open(data.downloadUrl, '_blank')
    },
    onSmartIntakeAnalysisReady (data) {
      if (!this.$store.getters.getUser) {
        return
      }

      // Calculate expiration time remaining if available
      let expirationText = ''
      if (data.expiresAt) {
        const expiresAt = new Date(data.expiresAt)
        const now = new Date()
        const minutesRemaining = Math.ceil((expiresAt - now) / (1000 * 60))
        expirationText = minutesRemaining > 0 ? ` Expires in ${minutesRemaining} minutes` : ' Link expired'
      }

      // Show toast notification with download link
      this.$toast.add({
        severity: 'success',
        summary: 'SmartIntake Analysis Ready',
        detail: `Analysis results ready to download.${expirationText}`,
        life: 8000,
        closable: true
      })
      window.open(data.downloadUrl, '_blank')
    },
    onProgress (progress, index) {
      this.notifications[index].progress = progress
    },
    async triggerScriptixTranscription (sessionId, documentId, index) {
      if (!sessionId || !documentId) {
        return
      }
      try {
        const transcriptionService = this.$store.getters.getUser.transcriptionServices
        let apiKey = null
        if (typeof transcriptionService === 'object' && transcriptionService.service && transcriptionService.service.name.toLowerCase() === 'scriptix') {
          apiKey = transcriptionService.apiKey
        }

        if (!apiKey) {
          return
        }
        const response = await this.$axios.downloadScriptixTranscript(sessionId, documentId, this.$store.getters.getUser.scriptix_api_key, (progress) => this.onProgress(progress, index))
        const locale = this.$store.getters.getUser.locale
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
        const downloadUrl = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = downloadUrl
        a.download = `transcription${Moment().locale(locale).format('DD_MMM_yyyy')}.doc` // The name for the downloaded file
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(downloadUrl)

        const deleteResponse = await this.$axios.deleteScriptixTranscript(sessionId, documentId, this.$store.getters.getUser.scriptix_api_key)
        if (deleteResponse.status === 204) {
          console.log('Transcription deleted successfully')
        }
      } catch (error) {
        console.error('There was an error downloading the file:', error)
      }
    }
  },
  async mounted () {
    console.log('Scriptix Transcription Modal Mounted')

    // Expose upload progress tracker on the root instance for MeetingRoom
    this.uploadProgressTracker = this.$refs.uploadProgress

    if (['development', 'acceptance', 'sandbox'].includes(process.env.VUE_APP_DD_ENV)) {
      // DEBUG: Make test method available globally for debugging
      window.testScriptixDialog = this.testScriptixDialog
    }

    this.$webrtc.EventBus.$on('onScriptixTranscriptionReceived', this.downloadTranscription)
    this.$webrtc.EventBus.$on('onBumicomTranscriptionReady', this.onBumicomTranscriptionReady)
    this.$webrtc.EventBus.$on('onSmartIntakeAnalysisReady', this.onSmartIntakeAnalysisReady)

    if (this.$store.getters.getProduct === 'personal') {
      clearInterval(this.$store.getters.getAlwaysAvailableInterval)
      this.$store.commit('setAlwaysAvailableInterval', null)
      const intervalId = setInterval(() => {
        if (this.$store.getters.getUser && this.$store.getters.getUser.always_call_available === true) {
          if (this.$store.getters.getMeetingStarted === false) {
            this.$webrtc.EventBus.$emit('onStatusChanged', {
              'status': 'online'
            })
          }
        }
      }, 15000)
      this.$store.commit('setAlwaysAvailableInterval', intervalId)
    }
  },
  beforeUnmount () {
    this.$webrtc.EventBus.$off('onScriptixTranscriptionReceived', this.downloadTranscription)
    this.$webrtc.EventBus.$off('onBumicomTranscriptionReady', this.onBumicomTranscriptionReady)
    this.$webrtc.EventBus.$off('onSmartIntakeAnalysisReady', this.onSmartIntakeAnalysisReady)
  },
  unmounted () {
    if (this.$store.getters.getProduct === 'personal') {
      clearInterval(this.$store.getters.getAlwaysAvailableInterval)
      this.$store.commit('setAlwaysAvailableInterval', null)
    }
  }
}
</script>

<style lang="scss" scoped>
  .hideDisconnected {
    display: none !important;
  }

  iframe {
    width: 100%;
    height: 100%;
  }
  .notification-count {
        width: 30px;
        height: 30px;
        position: absolute;
        bottom: 120px;
        right: 10px;
        background-color: #073C5A;
        color: white;
        border-radius: 50%;
        padding: 0.5rem;
        font-size: 0.8rem;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
        border: 1px solid white;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
    }
    :deep(.p-toast .p-toast-message.p-toast-message-warn),
    :deep(.p-toast .p-toast-message.p-toast-message-success),
    :deep(.p-toast .p-toast-message.p-toast-message-error) {
        background-color: #FFF;
        color: #000;
        .p-toast-message-content .p-toast-message-text .p-toast-detail {
            font-size: 0.7rem;
        }
    }
</style>
